/**
 * Where a signature box sits on a page, in PDF points.
 *
 * Fields are stored in PDF points — 612 x 792 for a letter page —
 * because that is what /api/sign/submit stamps the signature with. It
 * reads x, y, width and height straight through, with no scale of its
 * own. The screen is in CSS pixels. One number converts between the
 * two: the view scale, which is the base placement scale times however
 * far the signer has zoomed in.
 *
 * All of it lives here so the conversion happens in exactly one place.
 * A box dropped on a given line has to land on that line whatever the
 * zoom was when it was dropped — this is a recorded instrument, and a
 * signature in the wrong place is not a cosmetic bug.
 */

/** Pixels per PDF point in the placement view, before any zoom. */
export const PLACEMENT_BASE_SCALE = 1.35;

/** Pixels per PDF point at this zoom. */
export function placementViewScale(zoom) {
  const safe = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
  return PLACEMENT_BASE_SCALE * safe;
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * A new field dropped at a point inside the page, centred on the
 * click and kept on the paper.
 *
 * offsetX/offsetY are pixels from the page's top-left corner; maxW and
 * maxH are the page's size in points.
 */
export function newFieldBox({ offsetX, offsetY, viewScale, defW, defH, maxW, maxH }) {
  const x = (offsetX / viewScale) - (defW / 2);
  const y = (offsetY / viewScale) - (defH / 2);
  return {
    x: clamp(x, 0, Math.max(0, maxW - defW)),
    y: clamp(y, 0, Math.max(0, maxH - defH)),
    width: defW,
    height: defH,
  };
}

/** A field dragged by dxPx/dyPx from where it started. */
export function movedFieldBox({ origin, dxPx, dyPx, viewScale, width, height, maxW, maxH }) {
  return {
    x: clamp(origin.x + (dxPx / viewScale), 0, Math.max(0, maxW - width)),
    y: clamp(origin.y + (dyPx / viewScale), 0, Math.max(0, maxH - height)),
  };
}

/** A field resized by dxPx/dyPx from the size it started at. */
export function resizedFieldBox({ origin, dxPx, dyPx, viewScale, x, y, maxW, maxH }) {
  return {
    width: clamp(origin.width + (dxPx / viewScale), 24, Math.max(24, maxW - x)),
    height: clamp(origin.height + (dyPx / viewScale), 16, Math.max(16, maxH - y)),
  };
}
